'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

type UseCopyToClipboardOptions = {
  value?: string;
  timeout?: number;
};

const DEFAULT_TIMEOUT = 2000;

const fallbackCopy = (text: string) => {
  if (!text || typeof document === 'undefined') {
    return false;
  }

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand('copy');
  document.body.removeChild(textarea);
  return copied;
};

export function useCopyToClipboard({
  value,
  timeout = DEFAULT_TIMEOUT,
}: UseCopyToClipboardOptions = {}) {
  const [hasCopied, setHasCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const copy = useCallback(
    async (overrideValue?: string) => {
      const copyValue =
        overrideValue ??
        value ??
        (typeof window !== 'undefined' ? window.location.href : '');

      if (!copyValue) {
        return false;
      }

      let copied = false;

      try {
        if (navigator?.clipboard?.writeText) {
          await navigator.clipboard.writeText(copyValue);
          copied = true;
        }
      } catch (error) {
        // * Intentionally fall back to legacy copy behavior.
      }

      if (!copied) {
        try {
          copied = fallbackCopy(copyValue);
        } catch (error) {
          // * No-op: avoid throwing on unsupported clipboard access.
        }
      }

      if (copied) {
        setHasCopied(true);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
          setHasCopied(false);
        }, timeout);
      }

      return copied;
    },
    [timeout, value],
  );

  return { hasCopied, copy };
}
